import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useIntl } from "react-intl";

import { Box, Tooltip } from "@mui/material";

import {
  DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW,
  getMultiAnalysisMainPanelHeights,
  getMultiAnalysisMainPanelsExpandableHeight,
  readMultiAnalysisMainPanelFlexGrowFromSession,
  resizeMultiAnalysisMainPanelFlexGrow,
  writeMultiAnalysisMainPanelFlexGrowToSession,
  type IMultiAnalysisMainPanelFlexGrow,
  type TMultiAnalysisMainPanelResizeBoundary,
} from "../utils/main-panels-layout";

interface IMultiAnalysisPanelResizeHandleProps {
  disabled?: boolean;
  onResizeDelta: (deltaY: number) => void;
  onDoubleClickReset?: () => void;
}

export const MultiAnalysisPanelResizeHandle: FC<IMultiAnalysisPanelResizeHandleProps> = ({
  disabled = false,
  onResizeDelta,
  onDoubleClickReset,
}) => {
  const { formatMessage } = useIntl();
  const lastPointerYRef = useRef<number | null>(null);
  const onResizeDeltaRef = useRef(onResizeDelta);
  const onDoubleClickResetRef = useRef(onDoubleClickReset);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);

  useEffect(() => {
    onResizeDeltaRef.current = onResizeDelta;
  }, [onResizeDelta]);

  useEffect(() => {
    onDoubleClickResetRef.current = onDoubleClickReset;
  }, [onDoubleClickReset]);

  const stopDragging = useCallback(() => {
    isDraggingRef.current = false;
    lastPointerYRef.current = null;
  }, []);

  useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      if (!isDraggingRef.current || lastPointerYRef.current === null) {
        return;
      }

      const deltaY = event.clientY - lastPointerYRef.current;

      if (deltaY !== 0) {
        hasDraggedRef.current = true;
        onResizeDeltaRef.current(deltaY);
        lastPointerYRef.current = event.clientY;
      }
    };

    const handleWindowPointerUp = () => {
      stopDragging();
    };

    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
    window.addEventListener("pointercancel", handleWindowPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      window.removeEventListener("pointercancel", handleWindowPointerUp);
    };
  }, [stopDragging]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      lastPointerYRef.current = event.clientY;
    },
    [disabled],
  );

  const handleDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onDoubleClickResetRef.current?.();
  }, []);

  return (
    <Tooltip title={formatMessage({ id: "pages.multiAnalysis.layout.resizeHandleTooltip" })} arrow disableInteractive>
      <Box
        role="separator"
        aria-orientation="horizontal"
        aria-disabled={disabled}
        onPointerDown={handlePointerDown}
        onDoubleClick={handleDoubleClick}
        sx={{
          position: "relative",
          zIndex: 2,
          flex: "0 0 auto",
          height: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "ns-resize",
          touchAction: "none",
          userSelect: "none",
          "&:hover .multi-analysis-panel-resize-grip": disabled
            ? undefined
            : {
                bgcolor: "#9aa8c0",
              },
        }}
      >
        <Box
          className="multi-analysis-panel-resize-grip"
          sx={{
            width: 48,
            height: 4,
            borderRadius: 999,
            bgcolor: disabled ? "transparent" : "#c5cede",
            transition: "background-color 0.15s ease",
          }}
        />
      </Box>
    </Tooltip>
  );
};

interface IUseMultiAnalysisMainPanelsLayoutArgs {
  enabled: boolean;
  trendPanelCollapsed: boolean;
  trendPanelVisible: boolean;
}

export const useMultiAnalysisMainPanelsLayout = ({
  enabled,
  trendPanelCollapsed,
  trendPanelVisible,
}: IUseMultiAnalysisMainPanelsLayoutArgs) => {
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [panelFlexGrow, setPanelFlexGrow] = useState<IMultiAnalysisMainPanelFlexGrow>(() =>
    readMultiAnalysisMainPanelFlexGrowFromSession(),
  );

  const panelsContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      containerNodeRef.current = node;

      if (!node || !enabled) {
        setContainerHeight(0);
        return;
      }

      const updateHeight = () => {
        const nextHeight = node.clientHeight;

        if (nextHeight > 0) {
          setContainerHeight(nextHeight);
        }
      };

      updateHeight();

      const observer = new ResizeObserver(updateHeight);
      observer.observe(node);
      resizeObserverRef.current = observer;
    },
    [enabled],
  );

  const trendsExpanded = trendPanelVisible && !trendPanelCollapsed;
  const expandableHeight = useMemo(
    () => getMultiAnalysisMainPanelsExpandableHeight(containerHeight, trendsExpanded, trendPanelVisible),
    [containerHeight, trendPanelVisible, trendsExpanded],
  );

  const panelHeights = useMemo(
    () =>
      expandableHeight > 0 ? getMultiAnalysisMainPanelHeights(panelFlexGrow, expandableHeight, trendsExpanded) : null,
    [expandableHeight, panelFlexGrow, trendsExpanded],
  );

  const handleResizeDelta = useCallback(
    (boundary: TMultiAnalysisMainPanelResizeBoundary, deltaY: number) => {
      if (deltaY === 0) {
        return;
      }

      const measuredHeight = containerNodeRef.current?.clientHeight ?? containerHeight;
      const nextExpandableHeight = getMultiAnalysisMainPanelsExpandableHeight(
        measuredHeight,
        trendsExpanded,
        trendPanelVisible,
      );

      if (nextExpandableHeight <= 0) {
        return;
      }

      setPanelFlexGrow(currentFlexGrow => {
        const nextFlexGrow = resizeMultiAnalysisMainPanelFlexGrow(
          currentFlexGrow,
          nextExpandableHeight,
          boundary,
          deltaY,
          trendsExpanded,
        );

        writeMultiAnalysisMainPanelFlexGrowToSession(nextFlexGrow);

        return nextFlexGrow;
      });
    },
    [containerHeight, trendPanelVisible, trendsExpanded],
  );

  const handleTimeGraphicResizeDelta = useCallback(
    (deltaY: number) => handleResizeDelta("time-graphic", deltaY),
    [handleResizeDelta],
  );

  const handleGraphicTrendsResizeDelta = useCallback(
    (deltaY: number) => handleResizeDelta("graphic-trends", deltaY),
    [handleResizeDelta],
  );

  const handleResetPanelProportions = useCallback(() => {
    const defaultFlexGrow = { ...DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW };

    setPanelFlexGrow(defaultFlexGrow);
    writeMultiAnalysisMainPanelFlexGrowToSession(defaultFlexGrow);
  }, []);

  return {
    panelsContainerRef,
    panelFlexGrow,
    panelHeights,
    hasMeasuredLayout: expandableHeight > 0,
    handleTimeGraphicResizeDelta,
    handleGraphicTrendsResizeDelta,
    handleResetPanelProportions,
    isGraphicTrendsResizeEnabled: trendsExpanded,
  };
};
